import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Wallet, TrendingUp, Activity, ExternalLink, Trash2, Eye } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { walletsApi } from '../lib/api'
import { formatCurrency, shortenAddress } from '../lib/utils'

export function Wallets() {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [newWallet, setNewWallet] = useState({
    address: '',
    label: '',
    isWhale: false,
  })

  const { data: walletsResponse, isLoading } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => walletsApi.getAll().then(r => r.data),
  })

  const wallets = walletsResponse?.wallets || []

  const createMutation = useMutation({
    mutationFn: (data: any) => walletsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] })
      setShowAddModal(false)
      setNewWallet({ address: '', label: '', isWhale: false })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => walletsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallets'] }),
  })

  if (isLoading) {
    return <div className="text-center py-12">Loading wallets...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wallets</h1>
          <p className="mt-1 text-gray-500">Track and analyze wallet performance</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Wallet
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Wallet className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Wallets</p>
              <p className="text-2xl font-bold">{wallets?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Whale Wallets</p>
              <p className="text-2xl font-bold">
                {wallets?.filter((w: any) => w.isWhale).length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg Win Rate</p>
              <p className="text-2xl font-bold">
                {wallets?.length 
                  ? ((wallets.reduce((sum: number, w: any) => sum + (w.winRate || 0), 0) / wallets.length) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Avg PnL</p>
              <p className="text-2xl font-bold">
                {formatCurrency(
                  wallets?.length
                    ? wallets.reduce((sum: number, w: any) => sum + (w.totalPnL || 0), 0) / wallets.length
                    : 0
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Wallets Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Win Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PnL</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {wallets?.map((wallet: any) => (
              <tr key={wallet.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <span className="font-mono text-sm">{shortenAddress(wallet.address)}</span>
                    <a
                      href={`https://polygonscan.com/address/${wallet.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-gray-400 hover:text-blue-600"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-900">{wallet.label || '-'}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm font-medium ${(wallet.winRate || 0) > 0.5 ? 'text-green-600' : 'text-red-600'}`}>
                    {((wallet.winRate || 0) * 100).toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm font-medium ${(wallet.totalPnL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(wallet.totalPnL || 0)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {wallet.isWhale ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      Whale
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Normal
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Link
                      to={`/wallets/${wallet.id}`}
                      className="p-2 text-gray-400 hover:text-blue-600"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => deleteMutation.mutate(wallet.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {(!wallets || wallets.length === 0) && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No wallets tracked yet. Add your first wallet!</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Wallet Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Wallet</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wallet Address
                </label>
                <input
                  type="text"
                  value={newWallet.address}
                  onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  placeholder="0x..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label (optional)
                </label>
                <input
                  type="text"
                  value={newWallet.label}
                  onChange={(e) => setNewWallet({ ...newWallet, label: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="My Wallet"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isWhale"
                  checked={newWallet.isWhale}
                  onChange={(e) => setNewWallet({ ...newWallet, isWhale: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="isWhale" className="ml-2 text-sm text-gray-700">
                  Mark as Whale Wallet
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(newWallet)}
                disabled={!newWallet.address.startsWith('0x') || newWallet.address.length !== 42}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Add Wallet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
